import { AppContext } from "01Data/AppData";
import PageTemplate from "03AdminPages/00Templates/PageTemplate";
import { getFields } from "97Parts/Andon/logFields";
import { useModel, useSignal } from "99Tools/useModel";
import { CheckKizaiID, formatCellValue } from "99Tools/utils";
import { Backdrop, CircularProgress, styled, Typography } from "@mui/material";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";

const ColorHeader = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.grey[800],
}));

const ErrorText = styled(Typography)(({ theme }) => ({
  fontSize: "0.8rem",
  color: "#f00",
}));

/**
 * 漏水一覧
 * @returns TSX
 */
const LogBody = () => {
  const list = useContext(AppContext).dataAdmin.log;
  const rows = list.logItems
    .slice(
      list.page * list.rowsPerPage,
      list.page * list.rowsPerPage + list.rowsPerPage
    )
    .map((row: any, i: number) => {
      return (
        <TableRow hover tabIndex={-1} key={row.ItemId}>
          {getFields(list.kind).map((col) => (
            <TableCell key={`${col}_${i}`} align="center">
              <pre>{formatCellValue(col, row[col])}</pre>
            </TableCell>
          ))}
        </TableRow>
      );
    });

  return <TableBody>{rows}</TableBody>;
};

/**
 * ユーザテーブル
 * @returns TSX
 */
const LogTable = () => {
  const list = useContext(AppContext).dataAdmin.log;

  const handleChangePage = (event: unknown, newPage: number) => {
    list.page = newPage;
    list.changed();
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    list.rowsPerPage = +event.target.value;
    list.page = 0;
    list.changed();
  };

  const headers = getFields(list.kind).map((col) => (
    <ColorHeader key={col} align="center">
      {col}
    </ColorHeader>
  ));

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer sx={{ minHeight: "60vh" }}>
        <Table stickyHeader aria-label="sticky table" size="small">
          <TableHead style={{ whiteSpace: "nowrap" }}>
            <TableRow>{headers}</TableRow>
          </TableHead>
          <LogBody />
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={list.logItems.length}
        rowsPerPage={list.rowsPerPage}
        page={list.page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

/**
 * デバイスログ表示
 * @returns TSX
 */
const LogIDPage = () => {
  const d = useContext(AppContext).dataAdmin.log;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useModel(d);
  const redraw = useSignal();

  useEffect(() => {
    const id = searchParams.getAll("id");
    const last = searchParams.get("last") ?? "";
    if (id.length === 0) {
      navigate("/admin/list/");
    }
    const kind = CheckKizaiID(id[0]);
    if (kind === "error") {
      navigate("/admin/list/");
    }

    d.kind = kind;
    d.deviceId = id[0];

    if (last === "") {
      // 最終到達なし → 今日から1週間前まで
      const date = new Date();
      d.start = new Date(date.setDate(date.getDate() - 7))
        .toISOString()
        .split("T")[0]; // 1週間前
      d.end = new Date().toISOString().split("T")[0]; // 今日
    } else {
      // 最終到達あり → 最終到達から1週間前まで
      const date = new Date(last);
      d.start = new Date(date.setDate(date.getDate() - 7))
        .toISOString()
        .split("T")[0];
      d.end = last;
    }
    setLoading(true);
    d.GetLog()
      .catch((err: any) => {
        d.SetError(err, "デバイスログ取得失敗");
      })
      .finally(() => {
        setLoading(false);
        d.changed();
      });
  }, [d, navigate, searchParams]);

  const handleChangeStart = async (e: any) => {
    if (e.target.value > d.end) {
      setErrorMessage("開始日付は終了日付以前を選択してください。");
      return;
    } else {
      d.start = e.target.value;
      setErrorMessage("");
    }

    try {
      setLoading(true);
      await d.GetLog();
      redraw();
    } catch (err) {
      d.SetError(err, "デバイスログ取得失敗");
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEnd = async (e: any) => {
    if (d.start > e.target.value) {
      setErrorMessage("終了日付は開始日付以降を選択してください。");
      return;
    } else {
      d.end = e.target.value;
      setErrorMessage("");
    }

    try {
      setLoading(true);
      await d.GetLog();
      redraw();
    } catch (err) {
      d.SetError(err, "デバイスログ取得失敗");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageTemplate title={d.deviceId}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          gap: 10,
          marginBottom: 30,
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
          }}
        >
          <input type="date" value={d.start} onChange={handleChangeStart} />
          <div>～</div>
          <input type="date" value={d.end} onChange={handleChangeEnd} />
          <ErrorText>{errorMessage}</ErrorText>
        </div>
      </div>
      <LogTable />
      <Backdrop open={loading} sx={{ zIndex: 1201 }}>
        <CircularProgress sx={{ color: "white" }} />
      </Backdrop>
    </PageTemplate>
  );
};

export default LogIDPage;
